import type {
  AiChatNode,
  PickType,
  UiMessageChunkDto,
  UiStreamHooks,
} from "@intelligems/ig-types";

import { UiMessageChunkTypeGuardProcessor } from "./ui-message-chunk-type-guard-processor";
import { UiMessagePartTypeGuardProcessor } from "./ui-message-part-type-guard-processor";
import { getOrCreateNodeMessageById, getPartByIdAndMessageId } from "./utils";

export class TextProcessor {
  static isTextPart(
    part: UiMessageChunkDto
  ): part is
    | PickType<UiMessageChunkDto, "text-start">
    | PickType<UiMessageChunkDto, "text-delta">
    | PickType<UiMessageChunkDto, "text-end">
    | PickType<UiMessageChunkDto, "text"> {
    return (
      UiMessageChunkTypeGuardProcessor.isTextStartChunk(part) ||
      UiMessageChunkTypeGuardProcessor.isTextDeltaChunk(part) ||
      UiMessageChunkTypeGuardProcessor.isDeprecatedTextChunk(part) ||
      UiMessageChunkTypeGuardProcessor.isTextEndChunk(part)
    );
  }

  static processStreamPart(
    node: AiChatNode,
    messageId: string,
    streamPart: UiMessageChunkDto,
    hooks?: UiStreamHooks
  ): void {
    if (UiMessageChunkTypeGuardProcessor.isTextStartChunk(streamPart)) {
      this.textStart(node, messageId, streamPart);
    } else if (UiMessageChunkTypeGuardProcessor.isTextDeltaChunk(streamPart)) {
      this.concatTextDelta(node, messageId, streamPart, hooks);
    } else if (
      UiMessageChunkTypeGuardProcessor.isDeprecatedTextChunk(streamPart)
    ) {
      this.concatDeprecatedText(node, messageId, streamPart);
    } else if (UiMessageChunkTypeGuardProcessor.isTextEndChunk(streamPart)) {
      this.textEnd(node, messageId, streamPart);
      // const lastPart = getLastMessagePartFromMessageId(
      //   node.messages,
      //   messageId
      // );
      // if (
      //   lastPart &&
      //   UiMessagePartTypeGuardProcessor.isTextPart(lastPart) &&
      //   lastPart.state === "done"
      // ) {
      // }
    }
  }

  private static textStart(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "text-start">
  ) {
    const existingPart = getPartByIdAndMessageId(
      node.messages,
      messageId,
      streamPart.id
    );
    // Allow new text bubble if the previous one with this ID is already done
    // (happens in multi-step streams where the SDK reuses IDs across steps)
    if (
      existingPart &&
      UiMessagePartTypeGuardProcessor.isTextPart(existingPart) &&
      existingPart.state === "done"
    ) {
      // Mark the old part so delta/end target the new one
      if ("id" in existingPart) {
        existingPart.id = `${existingPart.id as string}-prev-${Date.now()}`;
      }
    } else if (existingPart) {
      return;
    }

    const requestUserInputToolExists = node.messages.some(
      (m) =>
        m.id === messageId &&
        m.parts.some(
          (p) =>
            p.type === "dynamic-tool" && p.toolName === "request-user-input"
        )
    );
    if (requestUserInputToolExists) {
      return;
    }

    const message = getOrCreateNodeMessageById(
      node,
      messageId,
      "assistant",
      streamPart.timestamp
    );

    message.parts.push({
      id: streamPart.id,
      type: "text",
      text: "",
      state: "streaming",
      timestamp: streamPart.timestamp,
    });
  }

  private static concatTextDelta(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "text-delta">,
    hooks?: UiStreamHooks
  ): string {
    const part = getPartByIdAndMessageId(
      node.messages,
      messageId,
      streamPart.id
    );

    if (part && UiMessagePartTypeGuardProcessor.isTextPart(part)) {
      part.text += streamPart.delta;
      part.state = "streaming";
      part.timestamp = streamPart.timestamp;

      hooks?.onText?.(part.text);
      return part.text;
    }
    return "";
  }

  private static concatDeprecatedText(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "text">
  ): string {
    const id = `deprecated-text-${messageId}`;
    let part = getPartByIdAndMessageId(node.messages, messageId, id);

    if (!part) {
      this.textStart(node, messageId, {
        type: "text-start",
        id: id,
        nodeName: node.name,
        nodeId: node.id,
        nodeType: "other",
        timestamp: streamPart.timestamp,
      });
      part = getPartByIdAndMessageId(node.messages, messageId, id);
    }

    if (part && UiMessagePartTypeGuardProcessor.isTextPart(part)) {
      part.text += streamPart.text;
      part.state = "streaming";
      part.timestamp = streamPart.timestamp;
      return part.text;
    }
    return "";
  }

  private static textEnd(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "text-end">
  ) {
    const part = getPartByIdAndMessageId(
      node.messages,
      messageId,
      streamPart.id
    );

    if (part && UiMessagePartTypeGuardProcessor.isTextPart(part)) {
      part.state = "done";
      part.timestamp = streamPart.timestamp;
    }
  }
}
